import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import axios from "axios";
import { 
  Receipt, 
  Building2, 
  CreditCard,
  Clock,
  CheckCircle2,
  XCircle,
  ChevronDown,
  ChevronUp,
  TrendingUp,
  Gift,
  Loader2,
  Euro,
  Calendar,
  RefreshCw,
  Download,
  FileText
} from "lucide-react";
import { Button } from "../../components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminInvoices = () => {
  const [invoicesByOrg, setInvoicesByOrg] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedOrgs, setExpandedOrgs] = useState({});
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportingOrgId, setExportingOrgId] = useState(null);

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/invoices/by-organization`);
      setInvoicesByOrg(response.data);
    } catch (error) {
      console.error("Error fetching invoices:", error);
      toast.error("Erreur lors du chargement des factures");
    } finally {
      setLoading(false);
    }
  };

  const exportAllPdf = async () => {
    try {
      setExportingPdf(true);
      const response = await axios.get(`${API}/admin/invoices/export/pdf`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ResponsIA_Factures_${new Date().toISOString().slice(0,10)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Export PDF téléchargé");
    } catch (error) {
      console.error("Error exporting PDF:", error);
      toast.error("Erreur lors de l'export PDF");
    } finally {
      setExportingPdf(false);
    }
  };

  const exportOrgPdf = async (orgId, orgName) => {
    try {
      setExportingOrgId(orgId);
      const response = await axios.get(`${API}/admin/invoices/export/${orgId}/pdf`, {
        responseType: 'blob'
      });
      
      // Create download link
      const safeName = orgName.replace(/[^a-zA-Z0-9]/g, '_');
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Factures_${safeName}_${new Date().toISOString().slice(0,10)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Export PDF de ${orgName} téléchargé`);
    } catch (error) {
      console.error("Error exporting org PDF:", error);
      toast.error("Erreur lors de l'export PDF");
    } finally {
      setExportingOrgId(null);
    }
  };

  const toggleOrg = (orgId) => {
    setExpandedOrgs(prev => ({
      ...prev,
      [orgId]: !prev[orgId]
    }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const formatAmount = (amount, currency = "eur") => {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: currency.toUpperCase()
    }).format(amount);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      paid: { color: "bg-emerald-100 text-emerald-700", icon: CheckCircle2, label: "Payé" },
      completed: { color: "bg-emerald-100 text-emerald-700", icon: CheckCircle2, label: "Complété" },
      active: { color: "bg-blue-100 text-blue-700", icon: CheckCircle2, label: "Actif" },
      trial: { color: "bg-amber-100 text-amber-700", icon: Gift, label: "Essai" },
      simulated: { color: "bg-purple-100 text-purple-700", icon: RefreshCw, label: "Simulé" },
      pending: { color: "bg-slate-100 text-slate-700", icon: Clock, label: "En attente" },
      failed: { color: "bg-red-100 text-red-700", icon: XCircle, label: "Échoué" },
      none: { color: "bg-slate-100 text-slate-500", icon: XCircle, label: "Aucun" }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  const getPlanBadge = (plan) => {
    if (!plan) return null;
    
    const planConfig = {
      essential: { color: "bg-blue-100 text-blue-700", label: "Essentiel" },
      premium: { color: "bg-violet-100 text-violet-700", label: "Premium" },
      subscription: { color: "bg-slate-100 text-slate-700", label: "Abonnement" }
    };
    
    const config = planConfig[plan] || planConfig.subscription;
    
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case "payment":
        return <CreditCard className="w-4 h-4 text-emerald-500" />;
      case "renewal":
      case "subscription_renewal":
        return <RefreshCw className="w-4 h-4 text-blue-500" />;
      case "subscription_payment":
        return <Receipt className="w-4 h-4 text-violet-500" />;
      default:
        return <Receipt className="w-4 h-4 text-slate-400" />;
    }
  };

  // Calculate totals
  const totalRevenue = invoicesByOrg.reduce((sum, org) => sum + org.total_paid, 0);
  const activeSubscriptions = invoicesByOrg.filter(org => 
    org.subscription?.status === "active" || org.subscription?.status === "trial"
  ).length;
  const trialCount = invoicesByOrg.filter(org => org.subscription?.is_trial).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-invoices">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-['Plus_Jakarta_Sans'] text-slate-900">
            Factures
          </h1>
          <p className="text-slate-500 mt-1">
            Historique des paiements par établissement
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={exportAllPdf}
            disabled={exportingPdf}
            className="bg-violet-600 hover:bg-violet-700 rounded-xl"
            data-testid="export-all-pdf"
          >
            {exportingPdf ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Export PDF
          </Button>
          <Button
            onClick={fetchInvoices}
            variant="outline"
            className="rounded-xl"
            data-testid="refresh-invoices"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualiser
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl p-6 border border-slate-100">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-emerald-50 rounded-xl flex items-center justify-center">
              <Euro className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{formatAmount(totalRevenue)}</p>
              <p className="text-sm text-slate-500">Revenu total</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-2xl p-6 border border-slate-100">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{activeSubscriptions}</p>
              <p className="text-sm text-slate-500">Abonnements actifs</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-2xl p-6 border border-slate-100">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-amber-50 rounded-xl flex items-center justify-center">
              <Gift className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{trialCount}</p>
              <p className="text-sm text-slate-500">En période d'essai</p>
            </div>
          </div>
        </div>
      </div>

      {/* Organizations List */}
      <div className="space-y-4">
        {invoicesByOrg.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 border border-slate-100 text-center">
            <Receipt className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">Aucune facture</h3>
            <p className="text-slate-500">Les factures apparaîtront ici une fois les premiers paiements effectués.</p>
          </div>
        ) : (
          invoicesByOrg.map((org) => (
            <div 
              key={org.organization_id}
              className="bg-white rounded-2xl border border-slate-100 overflow-hidden"
              data-testid={`org-invoices-${org.organization_id}`}
            >
              {/* Organization Header */}
              <div 
                className="p-6 cursor-pointer hover:bg-slate-50 transition-colors"
                onClick={() => toggleOrg(org.organization_id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-violet-50 rounded-xl flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-violet-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">{org.organization_name}</h3>
                      <p className="text-sm text-slate-500">{org.business_type}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-6">
                    {/* Subscription Status */}
                    <div className="hidden md:flex flex-col items-end gap-1">
                      <div className="flex items-center gap-2">
                        {getPlanBadge(org.subscription?.plan)}
                        {getStatusBadge(org.subscription?.status)}
                      </div>
                      {org.subscription?.is_trial && org.subscription?.trial_ends_at && (
                        <span className="text-xs text-amber-600">
                          Essai jusqu'au {formatDate(org.subscription.trial_ends_at).split(",")[0]}
                        </span>
                      )}
                    </div>
                    
                    {/* Total */}
                    <div className="text-right">
                      <p className="text-lg font-bold text-slate-900">{formatAmount(org.total_paid)}</p>
                      <p className="text-xs text-slate-500">{org.payment_count} paiement(s)</p>
                    </div>
                    
                    {/* Export PDF Button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        exportOrgPdf(org.organization_id, org.organization_name);
                      }}
                      disabled={exportingOrgId === org.organization_id}
                      className="p-2 text-slate-400 hover:text-violet-600 hover:bg-violet-50 rounded-lg transition-colors"
                      title="Exporter en PDF"
                      data-testid={`export-org-pdf-${org.organization_id}`}
                    >
                      {exportingOrgId === org.organization_id ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <FileText className="w-5 h-5" />
                      )}
                    </button>
                    
                    {/* Expand Button */}
                    <div className="p-2">
                      {expandedOrgs[org.organization_id] ? (
                        <ChevronUp className="w-5 h-5 text-slate-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-slate-400" />
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Mobile subscription status */}
                <div className="flex md:hidden items-center gap-2 mt-3">
                  {getPlanBadge(org.subscription?.plan)}
                  {getStatusBadge(org.subscription?.status)}
                </div>
              </div>
              
              {/* Invoices List (Expanded) */}
              {expandedOrgs[org.organization_id] && (
                <div className="border-t border-slate-100">
                  {org.invoices && org.invoices.length > 0 ? (
                    <div className="divide-y divide-slate-100">
                      {org.invoices.map((invoice, idx) => (
                        <div 
                          key={invoice.id || idx}
                          className="p-4 flex items-center justify-between hover:bg-slate-50"
                        >
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                              {getTypeIcon(invoice.type)}
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-slate-900 capitalize">
                                  {invoice.type === "payment" ? "Paiement" : 
                                   invoice.type === "renewal" || invoice.type === "subscription_renewal" ? "Renouvellement" :
                                   invoice.type === "subscription_payment" ? "Abonnement" : invoice.type}
                                </span>
                                {getPlanBadge(invoice.plan)}
                              </div>
                              <div className="flex items-center gap-2 text-sm text-slate-500">
                                <Calendar className="w-3 h-3" />
                                {formatDate(invoice.created_at)}
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-4">
                            {getStatusBadge(invoice.status)}
                            <span className="font-semibold text-slate-900">
                              {formatAmount(invoice.amount, invoice.currency)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="p-8 text-center text-slate-500">
                      <Receipt className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                      <p>Aucune facture pour cet établissement</p>
                      {org.subscription?.is_trial && (
                        <p className="text-sm text-amber-600 mt-1">En période d'essai gratuit</p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 rounded-2xl p-6 border border-blue-100">
        <h3 className="font-medium text-blue-900 mb-2">À propos des factures</h3>
        <p className="text-sm text-blue-800">
          Cette section affiche l'historique des paiements pour chaque établissement. 
          Les paiements "simulés" correspondent aux renouvellements automatiques du MVP. 
          Avec une vraie clé API Stripe, les paiements seront traités automatiquement.
        </p>
      </div>
    </div>
  );
};

export default AdminInvoices;
